import { Grid, Typography, Box, Button } from '@mui/material';
import React from 'react';
import { DateTime } from 'luxon';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Check, Delete } from '@mui/icons-material';
import { Title } from '../../../../components/atoms/title';
import { InfoInput, InfoItem } from './styles';
import api from '../../../../services/api';
import { colors } from '../../../../styles/colors';
import ResponsiveDialog from '../../../../components/molecules/responsiveDialog';

export function UbabyStatus({ ubabyData }) {
  const currencyFormatter = new Intl.NumberFormat('pt-br', { style: 'currency', currency: 'BRL' });
  const dateFormatter = date => DateTime.fromISO(date).setLocale('pt-BR').toLocaleString(DateTime.DATETIME_SHORT);
  const phoneFormatter = phone => phone.replace(/(\d{2})(\d{2})(\d{4,5})(\d{4})/, '+$1 ($2) $3-$4');
  const [deleteConfirmationDialog, setDeleteConfirmationDialog] = React.useState({ open: false, id: null });
  const [invites, setInvites] = React.useState(ubabyData.invites);

  const updateUbabyMutation = useMutation(
    async ubaby => {
      await api.put(`/ubabies/${ubabyData?.id}`, { ...ubaby });
    },
    {
      onSuccess: () => toast('Ubaby atualizado com sucesso!', { type: 'success' }),
      onError: () => {
        toast('Erro ao atualizar Ubaby!', { type: 'error' });
      },
    }
  );

  const handleSubmitForm = async e => {
    e.preventDefault();
    await updateUbabyMutation.mutateAsync({
      name: e.target.name.value,
      phone_number: e.target.phone_number.value.replace(/\D/g, ''),
      pix: e.target.pix.value,
    });
  };

  const handleDeleteInvite = async id => {
    try {
      await api.delete(`/partner_invites/${id}`);
      toast('Convite deletado com sucesso!', { type: 'success' });
      setInvites(invites.filter(invite => invite.id !== id));
    } catch (error) {
      toast('Erro ao deletar convite!', { type: 'error' });
    } finally {
      setDeleteConfirmationDialog({ open: false, id: null });
    }
  };

  const expirationCheck = expiration => DateTime.fromISO(expiration) < DateTime.now();

  return (
    ubabyData && (
      <>
        <Grid container>
          <Grid item>
            <Title>Status de Ubaby</Title>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box padding={2} marginBottom={4} border='1px solid #ccc' borderRadius='4px'>
              <form onSubmit={handleSubmitForm}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Grid container direction='column' alignItems='flex-start' spacing={1}>
                      <InfoItem>
                        <strong>ID: </strong>
                        {ubabyData.id}
                      </InfoItem>
                      <InfoItem>
                        <strong>Nome: </strong>
                        <InfoInput name='name' defaultValue={ubabyData.name} required />
                      </InfoItem>
                      <InfoItem>
                        <strong>Número de celular: </strong>
                        <InfoInput name='phone_number' defaultValue={phoneFormatter(ubabyData.phone_number)} required />
                      </InfoItem>
                      <InfoItem>
                        <strong>Países: </strong>
                        {ubabyData.countries.join(', ')}
                      </InfoItem>
                      <InfoItem>
                        <strong>PIX: </strong>
                        <InfoInput name='pix' defaultValue={ubabyData.pix} required />
                      </InfoItem>
                      <InfoItem>
                        <strong>Criado em: </strong>
                        {dateFormatter(ubabyData.created_at)}
                      </InfoItem>
                      <InfoItem>
                        <strong>Data do pagamento de 1ª semana: </strong>
                        {dateFormatter(ubabyData.first_payday)}
                      </InfoItem>
                      <InfoItem>
                        <strong>Valor devido da 1ª semana: </strong>
                        {currencyFormatter.format(ubabyData.first_payment ?? 0)}
                      </InfoItem>
                      <InfoItem>
                        <strong>Usuários trazidos no mês: </strong>
                        {ubabyData.month_count}
                      </InfoItem>
                      <InfoItem>
                        <strong>Valor devido: </strong>
                        {currencyFormatter.format(ubabyData.amount_owed ?? 0)}
                      </InfoItem>
                      <Button type='submit' style={{ marginTop: '12px', paddingLeft: '0', color: colors.primary_blue }}>
                        <Check /> Salvar Edição
                      </Button>
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant='h6' gutterBottom>
                      Pagamentos
                    </Typography>
                    {ubabyData.payments.length > 0 ? (
                      <ul>
                        {ubabyData.payments.map(payment => (
                          <li
                            key={payment.date}
                            style={{
                              listStyleType: payment.type === 'first week' ? 'none' : 'disc',
                              position: 'relative',
                              paddingLeft: payment.type === 'first week' ? '1.5em' : '0',
                            }}
                          >
                            {payment.type === 'first week' && (
                              <span
                                style={{
                                  position: 'absolute',
                                  left: 0,
                                }}
                              >
                                👶
                              </span>
                            )}
                            <strong>Valor: </strong>
                            {currencyFormatter.format(payment.amount)} | <strong>Data: </strong>
                            {dateFormatter(payment.date)}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <Typography>No payments available</Typography>
                    )}
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item>
            <Title>Convites</Title>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box padding={2} marginBottom={4} border='1px solid #ccc' borderRadius='4px'>
              {invites.map(invite => (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: '16px',
                  }}
                  key={invite.id}
                >
                  <div>
                    {expirationCheck(invite.expiration) ? (
                      <>
                        <strong style={{ color: colors.alert_red }}>Expirado</strong>
                        <br />
                      </>
                    ) : (
                      <>
                        <strong style={{ color: colors.success_green }}>Ativo</strong>
                        <br />
                      </>
                    )}

                    <strong>Nome: </strong>
                    {invite.name}
                    <br />
                    <strong>Criado em: </strong>
                    {dateFormatter(invite.created_at)}
                    <br />
                    <strong>Expira em: </strong>
                    {dateFormatter(invite.expiration)}
                    <br />
                    <strong>Usuários trazidos: </strong>
                    {invite.count}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                      style={{ color: colors.alert_red }}
                      onClick={() => setDeleteConfirmationDialog({ open: true, id: invite.id })}
                    >
                      <Delete /> Deletar convite
                    </Button>
                  </div>
                </div>
              ))}
            </Box>
          </Grid>
        </Grid>
        {deleteConfirmationDialog.open && (
          <ResponsiveDialog
            title='Deletar convite'
            open={deleteConfirmationDialog.open}
            handleClose={() => setDeleteConfirmationDialog({ open: false, id: null })}
            handleConfirm={() => handleDeleteInvite(deleteConfirmationDialog.id)}
            content='Tem certeza que deseja deletar esse convite?'
          />
        )}
      </>
    )
  );
}
